import React, { useState } from "react";
import { X as XIcon } from "lucide-react";
import { BpsAttachmentSlot } from "./BpsAttachmentSlot.jsx";
import { toLocalInput, fromLocalInput } from "../../lib/bps.js";

const INP = "w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30";

// Dialog for transitions that need extra data: QUOTED (cotação), SCHEDULE (datas), BILLED (factura).
export const BpsTransitionDialog = ({ bps, newState, enums, attachments, onAttachmentsChange, onConfirm, onClose, busy, error }) => {
  const [entry, setEntry] = useState(toLocalInput(bps.scheduled_entry_at));
  const [exit, setExit] = useState(toLocalInput(bps.expected_exit_at));
  const [invoice, setInvoice] = useState("");
  const [types, setTypes] = useState([]);
  const since = bps.admin_state_started_at || bps.created_at;
  const hasQuote = attachments.some((a) => a.category === "quotation" && a.uploaded_at >= since);

  const toggleType = (k) => setTypes((t) => (t.includes(k) ? t.filter((x) => x !== k) : t.length >= 2 ? t : [...t, k]));

  let canSubmit = true;
  if (newState === "QUOTED") canSubmit = hasQuote;
  if (newState === "SCHEDULE") canSubmit = Boolean(entry && exit);
  if (newState === "BILLED") canSubmit = Boolean(invoice.trim() && types.length);

  const submit = () => {
    if (newState === "SCHEDULE") onConfirm({ scheduled_entry_at: fromLocalInput(entry), expected_exit_at: fromLocalInput(exit) });
    else if (newState === "BILLED") onConfirm({ invoice_number: invoice.trim(), invoice_types: types });
    else onConfirm({});
  };

  const title = { QUOTED: "Passar a Orçado", SCHEDULE: "Agendar entrada", BILLED: "Registar factura" }[newState];
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="bps-transition-dialog">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="p-1 hover:bg-[#F4F6F8] rounded" data-testid="bps-transition-close"><XIcon className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          {newState === "QUOTED" && (
            <>
              <p className="text-sm text-[#6C757D]">Anexe a cotação para poder passar o caso a "Orçado".</p>
              <BpsAttachmentSlot bpsId={bps.id} category="quotation" required attachments={attachments} since={since} onUploaded={onAttachmentsChange} />
            </>
          )}
          {newState === "SCHEDULE" && (
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Entrada (data e hora) *</span>
                <input type="datetime-local" value={entry} onChange={(e) => setEntry(e.target.value)} className={INP} data-testid="bps-schedule-entry" />
              </label>
              <label className="block"><span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Previsão de saída *</span>
                <input type="datetime-local" value={exit} onChange={(e) => setExit(e.target.value)} className={INP} data-testid="bps-schedule-exit" />
              </label>
            </div>
          )}
          {newState === "BILLED" && (
            <>
              <label className="block"><span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Número da factura *</span>
                <input value={invoice} onChange={(e) => setInvoice(e.target.value)} className={`${INP} font-mono`} data-testid="bps-invoice-number" />
              </label>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-1">Tipo de factura * (um ou dois)</div>
                <div className="space-y-1.5">
                  {(enums.invoice_types || []).map((t) => (
                    <label key={t.key} className={`flex items-center gap-2 px-2 py-1.5 border rounded-sm cursor-pointer text-sm ${types.includes(t.key) ? "border-[#1F3864] bg-[#EEF2F8]" : "border-gray-200"}`}>
                      <input type="checkbox" checked={types.includes(t.key)} disabled={!types.includes(t.key) && types.length >= 2} onChange={() => toggleType(t.key)} className="accent-[#1F3864]" data-testid={`bps-invoice-type-${t.key}`} />
                      {t.label}
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
          {error && <div className="text-xs text-[#721C24] bg-[#F8D7DA] border border-[#DC3545]/30 rounded-sm px-3 py-2" data-testid="bps-transition-error">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-sm">Cancelar</button>
          <button onClick={submit} disabled={!canSubmit || busy} className="px-4 py-2 text-sm bg-[#1F3864] text-white rounded-sm disabled:opacity-50" data-testid="bps-transition-confirm">{busy ? "…" : "Confirmar"}</button>
        </div>
      </div>
    </div>
  );
};
